import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { store } from "../redux";
import {
  messaging,
  messagingReadyPromise,
  onMessageListener,
  registerFcmDevice,
  requestNotificationPermission,
} from "./firebase";
import { getToken } from "firebase/messaging";
import {
  addNotification,
  setNewNotificationTrue,
} from "../redux/reducers/notifications";
import { updateAiWheel } from "../redux/reducers/aiWheels";
import { useQueryClient } from "react-query";
import { isUserLoggedIn } from "./helpers";

const NotificationHandler = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // Reactive auth signal. Boolean() keeps this stable (`true`) across profile
  // edits — it flips only on login (null -> profile) and logout (profile ->
  // null), so the effect below re-runs exactly when auth toggles and never
  // re-registers listeners on an ordinary profile update.
  const isAuthed = useSelector((state) =>
    Boolean(state?.userProfile?.userProfile)
  );

  useEffect(() => {
    // A logged-out visitor must NEVER see the browser notification / FCM
    // permission prompt (Y11b). isUserLoggedIn() is the app-wide canonical auth
    // check (keyCloak token); isAuthed in the dep array re-runs this effect when
    // the user logs in during the same SPA session (login sets the token, then
    // dispatches userProfileSuccess) or logs out.
    if (!isUserLoggedIn()) {
      return;
    }

    const initialize = async () => {
      try {
        // Wait for firebase.js's async support check to settle -- messaging
        // can still be null here on a fresh mount purely because isSupported()
        // hasn't resolved yet, not because it's genuinely unsupported.
        await messagingReadyPromise;

        // Check if messaging is available before proceeding
        if (!messaging) {
          console.log("Firebase messaging not available in this browser");
          return;
        }

        // notif-0922-P2 (MEASURED web-dev, main.f306d778.js, Firefox): this effect
        // runs on mount/login with no user gesture. Firefox refuses (hangs/denies)
        // Notification.requestPermission() when it isn't called from inside a
        // short running click handler, so a Firefox visitor could never grant web
        // push. When permission is still "default" (undecided), the ONLY place
        // allowed to ask is a real click -- the Notifications page's "Enable
        // notifications" banner (src/pages/Notifications/EnableNotificationsBanner.jsx).
        // Once permission is already decided (granted/denied), asking again here
        // is harmless (no prompt UI, browsers auto-resolve) and keeps the FCM
        // token fresh across sessions, so that path still runs unconditionally.
        const permissionAlreadyDecided =
          typeof Notification === "undefined" || Notification.permission !== "default";

        if (permissionAlreadyDecided) {
          // Request notification permission first
          const permissionToken = await requestNotificationPermission();
          if (permissionToken) {
            console.log("FCM Permission Token received:", permissionToken);
          }
        }

        // Register service worker only if supported
        if ("serviceWorker" in navigator) {
          try {
            const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
            console.log("Service Worker registered with scope:", registration.scope);

            // Only get FCM token after service worker is registered and messaging is available.
            // getToken() itself calls Notification.requestPermission() internally whenever
            // permission is still "default" (see @firebase/messaging's getToken$1), so this is
            // a SECOND gesture-less call site that needs the same gate as above.
            if (messaging && permissionAlreadyDecided) {
              const fcmToken = await getToken(messaging, {
                vapidKey: "BJKnKWPgogNr6I513LAe6aeAilu4KjHgrYWTlrc4md3euzIuTD8LFGTQDHf8Am6LzLfw9HcINAyKlIGd42hD9i8",
              });
              console.log("FIREBASE == FCM Token:", fcmToken);
              // ISP-9999 (dev port of FE#6343): bind this token to the logged-in
              // user (POST /api/v1/devices/) -- see registerFcmDevice's own doc
              // comment in firebase.js for why this was previously a no-op.
              if (fcmToken) await registerFcmDevice(fcmToken);
            }
          } catch (swError) {
            console.warn("Service Worker registration failed:", swError);
          }
        }
      } catch (error) {
        console.warn("Notification initialization failed (this is normal in some browsers):", error);
      }
    };

    initialize();

    // onMessageListener awaits firebase.js's own readiness promise internally
    // and no-ops if genuinely unsupported, so it's always safe to call here
    // regardless of whether `messaging` has resolved yet on this render.
    onMessageListener((payload) => {
      console.log({ payload }, "NOTIFICATION PAYLOAD");
      if (payload.data.type == "15") {
        if (payload.data.status == "SUCCESS") {
          // Invalidate all wheels queries to trigger a refresh
          queryClient.invalidateQueries(["fetchWheels"]);
        }
        dispatch(
          updateAiWheel({
            id: payload.data.ref_id,
            ...payload.data,
          })
        );
      } else if (payload?.notification?.title) {
        store.dispatch(setNewNotificationTrue());
        store.dispatch(addNotification({
          id: payload.messageId,
          ...payload.notification,
          ...payload.data
        }));
      }
    });

    // Add message listener for background notifications only if service worker is supported
    if ('serviceWorker' in navigator) {
      try {
        navigator.serviceWorker.addEventListener('message', (event) => {
          console.log({event},"BACKGROUND NOTIFICATION")
          if (event.data.type === 'BACKGROUND_NOTIFICATION') {
            if(event.data.payload.data.type=="15"){
              // Parity with the foreground path (see above): a type-15 SUCCESS
              // push means an AI Wheel finished generating. Without invalidating
              // the wheels query the gallery stayed stale whenever the push
              // arrived while the tab was backgrounded — the new Wheel never
              // appeared until a manual refresh.
              if (event.data.payload.data.status == "SUCCESS") {
                queryClient.invalidateQueries(["fetchWheels"]);
              }
              dispatch(updateAiWheel({
                id: event.data.payload.data.ref_id,
                ...event.data.payload.data
              }))
            }
            else if (event.data.payload?.notification?.title) {
              store.dispatch(setNewNotificationTrue());
              store.dispatch(addNotification({ 
                id: event.data.payload.messageId, 
                ...event.data.payload.notification,
                ...event.data.payload.data 
              }));
            }
          }
        });
      } catch (swListenerError) {
        console.warn("Failed to add service worker event listener:", swListenerError);
      }
    }
  }, [dispatch, queryClient, isAuthed]);

  return null;
};

export default NotificationHandler;
